import { computeDebtorBalance } from 'app/Private/Clients/Detail/BalanceTab/ComputeDebtorBalance';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { sumBy } from 'remeda';
import type { DebtorBalance, DebtorBalanceInvoice } from 'shared/hooks/use-load-debtor-balance';

export type OverviewBar = {
  gross: number;
  aTraiter: number;
  enCours: number;
  notDue: number;
  credited: number;
};

const owedAmount = (invoice: DebtorBalanceInvoice): number => invoice.remainingBalance;

export function computeOverviewBar(
  debtorBalance: DebtorBalance,
  actionableInvoiceIds: ReadonlySet<string>,
  now: Dayjs = dayjs(),
): OverviewBar {
  const { totalBilled, totalPaymentsSum, totalCreditNotesSum, openInvoices } =
    computeDebtorBalance(debtorBalance);

  const gross = totalBilled;
  const netOwed = Math.max(totalBilled - totalPaymentsSum - totalCreditNotesSum, 0);
  const credited = Math.max(gross - netOwed, 0);

  const isDue = (invoice: DebtorBalanceInvoice): boolean => !dayjs(invoice.dueDate).isAfter(now);
  const isActionable = (invoice: DebtorBalanceInvoice): boolean =>
    actionableInvoiceIds.has(String(invoice.id));

  const dueInvoices = openInvoices.filter(isDue);
  const owedATraiter = sumBy(dueInvoices.filter(isActionable), owedAmount);
  const owedEnCours = sumBy(
    dueInvoices.filter(invoice => !isActionable(invoice)),
    owedAmount,
  );
  const owedNotDue = sumBy(
    openInvoices.filter(invoice => !isDue(invoice)),
    owedAmount,
  );

  if (owedATraiter + owedEnCours + owedNotDue <= 0) {
    return { gross, aTraiter: netOwed, enCours: 0, notDue: 0, credited };
  }

  let credit = Math.max(owedATraiter + owedEnCours + owedNotDue - netOwed, 0);
  const enCours = Math.max(owedEnCours - credit, 0);
  credit = Math.max(credit - owedEnCours, 0);
  const aTraiter = Math.max(owedATraiter - credit, 0);
  credit = Math.max(credit - owedATraiter, 0);
  const notDue = Math.max(owedNotDue - credit, 0);

  return { gross, aTraiter, enCours, notDue, credited };
}
